import { supabase } from '../lib/supabase';
import { LemitCota, ProspeccaoJob, ProspeccaoJobTipo } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────
// Fila da Prospecção (Lista Fria).
//
// Antes os lotes de "Localizar no mapa" / "Enriquecer" rodavam num `for` dentro
// do componente: trocar de tela desmontava a view e o lote morria no meio.
// Agora a UI só ENFILEIRA e quem trabalha é a edge function
// `processar-fila-prospeccao`, chamada de minuto em minuto pelo pg_cron. Fechar
// o navegador não interrompe nada.
//
// Os jobs da Lemit ainda respeitam a janela noturna (22h) e o teto diário da
// conta — configuráveis em Config › APIs.
// ─────────────────────────────────────────────────────────────────────────────

/** Statuses em que o job ainda tem (ou pode voltar a ter) trabalho pela frente. */
const STATUS_ATIVOS = ['pendente', 'rodando', 'pausado_janela', 'pausado_cota', 'erro'];

export interface CriarJobParams {
    tipo: ProspeccaoJobTipo;
    /** place_ids congelados no momento do clique — o job não segue o filtro depois. */
    placeIds: string[];
    criadoPor?: string | null;
    criadoPorNome?: string | null;
    /**
     * Horário (BRT, "22:00") em que o lote pode rodar. Omitir = **agora**, a
     * qualquer hora — é o que os botões de ação direta fazem. O agendamento
     * (AgendarLoteModal) é quem preenche isto.
     */
    janelaInicio?: string | null;
    janelaFim?: string | null;
    /** Teto de itens por dia DESTE lote. Omitir = vai até a cota da conta acabar. */
    maxPorDia?: number | null;
    /** true = respeita o teto diário da conta Lemit (sempre true nos jobs de Lemit). */
    usaCotaLemit: boolean;
}

/**
 * Cria o job e enfileira os itens. Devolve o job para a UI já mostrar o banner.
 *
 * Os itens vão em lotes de 500 porque um job de lista fria pode ter 10 mil
 * linhas e o PostgREST não engole um insert desse tamanho de uma vez.
 */
export async function criarJob(params: CriarJobParams): Promise<ProspeccaoJob> {
    const places = Array.from(new Set(params.placeIds.filter(Boolean)));
    if (places.length === 0) throw new Error('Nenhuma linha para enfileirar.');

    const janelaInicio = params.janelaInicio || null;
    const { data: job, error } = await supabase
        .from('prospeccao_jobs')
        .insert({
            tipo: params.tipo,
            criado_por: params.criadoPor ?? null,
            criado_por_nome: params.criadoPorNome ?? null,
            janela_inicio: janelaInicio,
            janela_fim: janelaInicio ? (params.janelaFim || '23:59') : null,
            max_por_dia: params.maxPorDia ?? null,
            // Redundante com `janela_inicio != null`, mas a coluna é NOT NULL e
            // versões antigas do worker ainda a leem.
            usa_janela: !!janelaInicio,
            usa_cota_lemit: params.usaCotaLemit,
            total: places.length,
        })
        .select('*')
        .single();
    if (error) throw error;

    for (let i = 0; i < places.length; i += 500) {
        const { error: itemErr } = await supabase
            .from('prospeccao_job_itens')
            .insert(places.slice(i, i + 500).map((place_id) => ({ job_id: job.id, place_id })));
        if (itemErr) {
            // Sem itens o job ficaria "rodando" para sempre sem nada a fazer.
            await supabase.from('prospeccao_jobs').delete().eq('id', job.id);
            throw itemErr;
        }
    }

    // Cutuca o worker para não esperar até 60s pelo próximo tick do cron. Se o
    // job for agendado, o claim ignora e ele volta a dormir — por isso é
    // best-effort e o erro não sobe.
    if (!janelaInicio) {
        supabase.functions.invoke('processar-fila-prospeccao', { body: {} }).catch(() => { /* o cron pega */ });
    }

    // O banner da fila consulta devagar quando não há lote: avisa que agora há
    window.dispatchEvent(new Event(EVENTO_JOB_CRIADO));
    return job as ProspeccaoJob;
}

/** Disparado quando um lote é criado nesta aba — o FilaProspeccaoBanner acorda na hora. */
export const EVENTO_JOB_CRIADO = 'prospeccao:job-criado';

/** Jobs que ainda importam para a UI (inclui os parados em 'erro', p/ o motivo aparecer). */
export async function getJobsAtivos(): Promise<ProspeccaoJob[]> {
    const { data, error } = await supabase
        .from('prospeccao_jobs')
        .select('*')
        .in('status', STATUS_ATIVOS)
        .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []) as ProspeccaoJob[];
}

export async function cancelarJob(id: string): Promise<void> {
    const { error } = await supabase
        .from('prospeccao_jobs')
        .update({ status: 'cancelado', heartbeat_em: null, concluido_em: new Date().toISOString() })
        .eq('id', id);
    if (error) throw error;
}

/** Consumo da conta Lemit hoje (inclui as consultas manuais do time). */
export async function getCotaLemit(): Promise<LemitCota> {
    const { data, error } = await supabase.rpc('lemit_cota_hoje');
    if (error) throw error;
    const r = Array.isArray(data) ? data[0] : data;
    return { usados: r?.usados ?? 0, limite: r?.limite ?? 500, restante: r?.restante ?? 0 };
}

export interface JanelaNoturna { inicio: string; fim: string; }

export async function getJanelaNoturna(): Promise<JanelaNoturna> {
    const { data } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['prospeccao_janela_inicio', 'prospeccao_janela_fim']);
    const get = (k: string, def: string) => (data || []).find((r: any) => r.key === k)?.value?.trim() || def;
    return { inicio: get('prospeccao_janela_inicio', '22:00'), fim: get('prospeccao_janela_fim', '23:59') };
}

/** Erros por item de um job — o "por quê" quando o disjuntor dispara. */
export async function getErrosDoJob(jobId: string, limite = 20): Promise<Array<{ place_id: string; erro: string }>> {
    const { data } = await supabase
        .from('prospeccao_job_itens')
        .select('place_id, erro')
        .eq('job_id', jobId)
        .eq('status', 'falhou')
        .not('erro', 'is', null)
        .limit(limite);
    return (data || []) as Array<{ place_id: string; erro: string }>;
}

// ── Rótulos ───────────────────────────────────────────────────────────────────

export const LABEL_TIPO: Record<ProspeccaoJobTipo, string> = {
    geocode: 'Localizando no mapa',
    lemit_documento: 'Enriquecendo documentos',
    lemit_dono: 'Buscando contato dos donos',
};

/** Quantas noites o job leva, dado o que sobra do teto por dia. */
export function noitesEstimadas(pendentes: number, porNoite: number): number {
    if (porNoite <= 0) return 0;
    return Math.max(1, Math.ceil(pendentes / porNoite));
}

/** ETA em texto, extrapolando o ritmo médio já observado no job. */
export function etaTexto(job: ProspeccaoJob): string {
    if (!job.iniciado_em || job.processados < 2) return '';
    const decorrido = Date.now() - new Date(job.iniciado_em).getTime();
    const porItem = decorrido / job.processados;
    const restMs = Math.max(0, (job.total - job.processados) * porItem);
    const min = Math.floor(restMs / 60000);
    if (min >= 60) return `~${Math.round(min / 60)} h restantes`;
    if (min >= 1) return `~${min} min restantes`;
    return `~${Math.round(restMs / 1000)}s restantes`;
}
